import { getToken } from 'firebase/messaging';
import api from 'src/redux/config';
import { getFirebaseMessaging, FCM_VAPID_KEY } from 'src/firebase';

// Kept so logout can remove this exact token server-side — there's no
// other way to retrieve it later, getToken() aside, and calling that
// again would require re-prompting permission logic unnecessarily.
const FCM_TOKEN_STORAGE_KEY = 'fcmWebToken';

// Registers this browser for web push (FCM) and saves the token so the
// backend's existing sendFcmToUser/sendFcmV1 pipeline (already used for
// Android/iOS) can also reach this tab, even when it isn't focused/open.
// Safe to call repeatedly — getToken() returns the same token if one
// already exists, and save-fcm-token upserts by token.
//
// Browsers only reliably show the permission prompt in response to a user
// gesture (Chrome otherwise suppresses it to a quiet address-bar icon), so
// the prompt is only shown when `prompt` is true — i.e. when called from a
// click handler. Automatic calls (on login) just register silently if the
// permission was already granted.
export async function registerWebPush({ prompt = false } = {}) {
  try {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;

    // Don't prompt if the user already denied it — the browser won't show
    // the prompt again anyway, and this skips the FCM setup work entirely.
    if (Notification.permission === 'denied') return null;
    if (Notification.permission === 'default' && !prompt) return null;

    const permission =
      Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    // register() can resolve while the worker is still installing — getToken()
    // needs an ACTIVE worker or it throws "no active Service Worker". `ready`
    // resolves only once activation is done.
    const registration = await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    if (!token) return null;

    await api.post('/user/save-fcm-token', {
      fcmToken: token,
      platform: 'web',
      lang: localStorage.getItem('i18nextLng') || undefined
    });
    localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);

    return token;
  } catch (error) {
    // Push is an enhancement, not a requirement — a failure here (blocked
    // permission, unsupported browser, network hiccup) shouldn't disrupt
    // the rest of the notification system (sockets still work).
    console.error('[webPush] registration failed', error);
    return null;
  }
}

// Removes this browser's push token server-side on logout — without this,
// a logged-out browser keeps receiving push notifications for the account
// that was logged in, since the browser-level push subscription and the
// server-side token record are both independent of the login session.
// Must run BEFORE the access token is cleared (remove-fcm-token requires
// auth), and must never block logout itself on failure.
export async function unregisterWebPush() {
  const token = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  if (!token) return;

  try {
    await api.post('/user/remove-fcm-token', { fcmToken: token });
  } catch (error) {
    console.error('[webPush] token removal failed', error);
  } finally {
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
  }
}
